import React from 'react';
import assert from 'assert';
import { AppContainer } from 'react-hot-loader';
import { BrowserRouter } from 'react-router-dom';
import { render } from 'react-dom';
import Raven from 'raven-js';

import createApiClient from '../api/createApiClient';
import { ANALYTICS_EVENT_USER_SESSION } from './analytics/eventList';
import analytics from './analytics';
import log from './lib/log';
import getStoreFromBrowser from './core/getStoreFromBrowser';
import initDevelopmentEnv from './core/developmentEnv';
import './style/style.less';

/**
 * This is the entry file for the client application.
 * This file, when required, initializes the client in the browser.
 */
assert(
  process.browser,
  'Only browsers are allowed to bootstrap the client'
);

// Gets the serialized state injected by the server.
// @see server/renderer
const store = getStoreFromBrowser();

// Initializes the development environment.
if (process.env.NODE_ENV !== 'production') {
  initDevelopmentEnv(store);
}

// Configures the analytics lib.
analytics.configure({
  store: store,
  _window: global.window,
});

// Configures Sentry error reporting.
const env = store.get(['env']);
if (env.SENTRY_BROWSER_KEY) {
  try {
    Raven.config(env.SENTRY_BROWSER_KEY, {
      tags: {
        NODE_ENV: env.NODE_ENV,
        APP_URL: env.APP_URL,
        BROWSER: true,
      },
    }).install();

    const currentUser = store.get(['currentUser']);
    Raven.setUserContext(currentUser);
  }
  catch (error) {
    log.error(error);
  }
}

// Track user visit.
analytics.identify();

if (store.get(['analytics', 'shouldTrackNewSession'])) {
  analytics.track(ANALYTICS_EVENT_USER_SESSION);
  store.set(['analytics', 'shouldTrackNewSession'], false);
}

// Loads the application in the DOM.
const containerNode = global.document.getElementById('react-container');

const renderAppInDOM = () => {
  const Root = require('./core/Root').default;

  // Inject API client.
  const apiClient = createApiClient({
    commitHash: store.get(['env', 'COMMIT_HASH']),
  });

  const rootedApp = (
    <AppContainer>
      <BrowserRouter>
        <Root tree={store} apiClient={apiClient} />
      </BrowserRouter>
    </AppContainer>
  );

  render(rootedApp, containerNode);
};

if (module.hot) {
  module.hot.accept('./core/Root', renderAppInDOM);
}

renderAppInDOM();
